import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { RegistrationBackButton } from "@/features/auth/components/RegistrationBackButton";
import {
  getRegistrationState,
  setCountry,
} from "@/features/auth/store/registration.store";
import flagEcuador from "@/assets/flags/bandera-ecuador.png";
import flagPeru from "@/assets/flags/bandera-peru.png";
import flagUsa from "@/assets/flags/bandera-estados-unidos.svg";

type Country = "EC" | "PE" | "US";

const COUNTRIES: { code: Country; nameKey: string; flag: string }[] = [
  { code: "EC", nameKey: "register:country.option.ecuador", flag: flagEcuador },
  { code: "PE", nameKey: "register:country.option.peru", flag: flagPeru },
  { code: "US", nameKey: "register:country.option.unitedStates", flag: flagUsa,  },
];

export function AuthCountrySelectPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("register");

  useEffect(() => {
    const { correlationId } = getRegistrationState();
    if (!correlationId) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  function handleSelect(code: Country) {
    setCountry(code);
    navigate("/register/identity", { replace: true });
  }

  return (
    <AuthEntryCard variant="country">
      <section className="mt-11 w-full max-w-[22rem] text-center">
        <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
          {t("register:country.title")}
        </h1>
        <p className="mt-3.5 text-[0.8125rem] leading-6 text-text-muted">
          {t("register:country.description")}
        </p>
      </section>

      <div className="mt-11 flex w-full max-w-96 flex-col gap-5">
        {COUNTRIES.map(({ code, nameKey, flag }) => (
          <button
            aria-label={t(nameKey)}
            className="flex w-full items-center gap-4 rounded-xl border-2 border-[#d7e4ef] bg-surface-card px-5 py-[1.125rem] text-left shadow-sm transition-[border-color,box-shadow] duration-150 hover:border-brand-primary hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            key={code}
            onClick={() => handleSelect(code)}
            type="button"
          >
            <img
              alt={t(nameKey)}
              className="block h-[1.375rem] w-8 shrink-0 rounded object-cover"
              src={flag}
            />
            <span className="flex-1 text-sm font-semibold text-text-primary">
              {t(nameKey)}
            </span>
            <span aria-hidden="true" className="shrink-0 text-xl leading-none text-text-muted">
              ›
            </span>
          </button>
        ))}
        <div className="mt-8 flex w-full max-w-96 items-center justify-center gap-3">
          <RegistrationBackButton />
        </div>
      </div>
    </AuthEntryCard>
  );
}
